import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react'; // Add this import at the top
import './styles.css';
import { isOfferDay, getNextOfferDay, isFoodlesSaturdayOffer, isPizzaBitesOfferDay } from '../utils/dateUtils';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      // Handle scroll event if needed
    };
    window.addEventListener('scroll', handleScroll);
    
    // Trigger entrance animations
    setTimeout(() => setIsLoaded(true), 100);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAboutClick = () => {
    navigate('/campus'); // Changed from '/about' to '/select-location'
  };

  return (
    <div className="relative h-screen overflow-hidden bg-black" style={{ marginTop: '-100px' }}>

      {/* Background GIF */}
      <div 
        className="absolute inset-0 bg-cover bg-center w-full h-full"
        style={{ backgroundImage: `url('/images/FOODLES1.gif')`, backgroundSize: '500px', backgroundRepeat: 'no-repeat' }}
      >
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50" />
      </div>

      {/* Background patterns */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#242_1px,transparent_1px),linear-gradient(-45deg,#111_2px,transparent_1px)] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,#222_1px,transparent_1px),linear-gradient(90deg,#111_2px,transparent_1px)] bg-[size:40px_40px] opacity-50" />
      </div>

      {/* Pop-up GIF with Click Here text */}
      <div 
        className={`absolute top-3/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-transform duration-1000 ease-out ${
          isLoaded ? 'translate-y--30 opacity-70 hover:opacity-100' : 'translate-y-20 opacity-0'
        }`}
        onClick={handleAboutClick}
      >
        <img 
          src="/images/ORDER.gif" 
          alt="Order Now"
          className="w-30 h-10 animate-slide mb-2" // Added margin bottom
        />
        <div className="text-center text-xs text-purple-500 font-mono animate-pulse">
          [ CLICK HERE ]
        </div>
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#111_1px,transparent_1px),linear-gradient(-45deg,#111_1px,transparent_1px)] bg-[size:20px_20px] opacity-20" />
      </div>
    </div>
  );
};

const PromotionalBanner = () => {
  const navigate = useNavigate();
  const [currentStatus, setCurrentStatus] = useState({ isLive: false });

  useEffect(() => {
    const checkOffer = () => {
      const isActive = isFoodlesSaturdayOffer();
      setCurrentStatus({ isLive: isActive });
    };

    checkOffer();
    const interval = setInterval(checkOffer, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleOfferClick = () => {
    if (currentStatus.isLive) {
      navigate('/offer');
    }
  };

  if (!currentStatus.isLive) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 overflow-hidden h-12 bg-black/90 backdrop-blur-sm border-t border-purple-500/20">
        {/* Gradient and glow effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/5 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,192,203,0.1)_0%,transparent_100%)]" />
        
        {/* Scrolling text container */}
        <div className="relative flex overflow-x-hidden">
          <div className="animate-[marquee_20s_linear_infinite] whitespace-nowrap py-3">
            {[...Array(3)].map((_, i) => (
              <span key={i} className="mx-4 text-purple-500 font-anon">
                ► FOODLES_SATURDAY [10% OFF] @ WEEKEND SPECIAL ►
              </span>
            ))}
          </div>
          
          {/* Create a duplicate for seamless loop */}
          <div className="absolute top-0 animate-[marquee2_20s_linear_infinite] whitespace-nowrap py-3">
            {[...Array(3)].map((_, i) => (
              <span key={i} className="mx-4 text-purple-500 font-mono">
                ► FOODLES_SATURDAY [10% OFF] @ WEEKEND SPECIAL ►
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Notification */}
      <div className="fixed top-40 right-1 z-50 space-y-3 w-64">
        <div 
          onClick={handleOfferClick}
          className="bg-black/90 backdrop-blur-sm text-purple-500 p-3 rounded-lg flex items-start gap-2 font-mono text-xs border border-purple-500/20 shadow-lg animate-slideInRight transform hover:scale-102 transition-all duration-1000 hover:border-purple-500/40 hover:-translate-y-0.5 cursor-pointer"
        >
          <Info className="w-3 h-3 mt-0.5 animate-pulse" />
          <div>
            <div className="font-bold tracking-wide flex items-center gap-2">
              FOODLES_SATURDAY
              <span className="text-[10px] bg-purple-500/10 text-purple-500 px-2 py-0.5 rounded animate-pulse">
                LIVE
              </span>
            </div>
            <div className="text-[10px] text-purple-300/90 mt-0.5">
              → Click here to view offer
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const FuturisticHomepage = () => {
  // Remove prefetchedStatus since it's only used for storage
  // eslint-disable-next-line no-unused-vars
  const [_, setPrefetchedStatus] = useState(null);
  
  // Remove unused navigate if not needed
  useEffect(() => {
    const prefetchRestaurantStatus = async () => {
      try {
        const API_URL = process.env.NODE_ENV === 'production'
          ? 'https://foodles-backend-lpzp.onrender.com'
          : 'http://localhost:5000';

        const response = await fetch(`${API_URL}/api/restaurants/status`);
        const data = await response.json();
        setPrefetchedStatus(data);
        
        // Store in sessionStorage for About page
        sessionStorage.setItem('prefetchedStatus', JSON.stringify({
          data,
          timestamp: Date.now()
        }));

        console.log('✓ Restaurant status prefetched');
      } catch (error) {
        console.error('Failed to prefetch restaurant status:', error);
      }
    };

    prefetchRestaurantStatus();

    // Clear old statuses but keep prefetched data
    return () => {
      const keysToKeep = ['prefetchedStatus'];
      Object.keys(sessionStorage).forEach(key => {
        if (!keysToKeep.includes(key)) {
          sessionStorage.removeItem(key);
        }
      });
      localStorage.clear();
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="pt-24" style={{ marginTop: '-100px' }}>
        <Hero />
      </div>
      <PromotionalBanner />
    </div>
  );
};

export default FuturisticHomepage;